import React, { useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { motion } from "framer-motion";
import { db } from "../firebase/firebase-config";

const initialForm = {
  name: "",
  accountHolderName: "",
  mobileNumber: "",
  bankType: "",
  accountDetails: "",
  ifscCode: "",
  website: "",
};

const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const inMobile10Regex = /^[6-9]\d{9}$/;

const BankAccountPublicEntry = () => {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "mobileNumber") {
      nextValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "ifscCode") {
      nextValue = value.toUpperCase().trim();
    }

    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!form.name.trim()) {
      setErrorMessage("Name is required.");
      return;
    }

    if (!form.accountHolderName.trim()) {
      setErrorMessage("Account Holder Name is required.");
      return;
    }

    if (!inMobile10Regex.test(form.mobileNumber)) {
      setErrorMessage("Enter a valid 10-digit mobile number.");
      return;
    }

    if (!form.bankType.trim()) {
      setErrorMessage("Bank is required.");
      return;
    }

    if (!form.accountDetails.trim()) {
      setErrorMessage("Account Number is required.");
      return;
    }

    if (form.accountDetails.replace(/\s+/g, "").length < 6) {
      setErrorMessage("Enter a valid account number.");
      return;
    }

    if (!ifscRegex.test(form.ifscCode)) {
      setErrorMessage("Enter a valid IFSC code.");
      return;
    }

    if (form.website.trim()) {
      setErrorMessage("Unable to submit right now. Please refresh and try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "bank-account-submissions"), {
        name: form.name.trim(),
        accountHolderName: form.accountHolderName.trim(),
        mobileNumber: form.mobileNumber.trim(),
        bankType: form.bankType.trim(),
        accountDetails: form.accountDetails.replace(/\s+/g, "").trim(),
        ifscCode: form.ifscCode.trim(),
        status: "submitted",
        createDate: new Date().toISOString(),
        submittedFrom: "public-link",
      });

      setForm(initialForm);
      setSuccessMessage("Bank account details submitted successfully.");
    } catch (error) {
      console.error("Unable to submit bank account details:", error);
      setErrorMessage("Unable to submit right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="container-fluid py-4 px-2 px-md-3"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(34,197,94,0.1), transparent 24%), radial-gradient(circle at bottom right, rgba(59,130,246,0.1), transparent 28%), #f8fbff",
      }}
    >
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="row justify-content-center">
          <div className="col-12 col-xl-8">
            <div
              className="rounded-5 p-4 p-md-5"
              style={{
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(148,163,184,0.18)",
                boxShadow: "0 22px 44px rgba(15,23,42,0.08)",
              }}
            >
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12 d-none">
                    <label className="form-label fw-bold">Website</label>
                    <input
                      type="text"
                      className="form-control"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      autoComplete="off"
                      tabIndex="-1"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Account Holder Name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="accountHolderName"
                      value={form.accountHolderName}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Mobile</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="mobileNumber"
                      value={form.mobileNumber}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Bank</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="bankType"
                      value={form.bankType}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Account Number</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="accountDetails"
                      value={form.accountDetails}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">IFSC Code</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="ifscCode"
                      value={form.ifscCode}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-success btn-lg mt-4"
                  disabled={isSubmitting}
                  style={{ borderRadius: 16, minWidth: 220 }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Bank Account"}
                </button>
              </form>

              {successMessage && <div className="alert alert-success rounded-4 mt-4 mb-0">{successMessage}</div>}
              {errorMessage && <div className="alert alert-danger rounded-4 mt-4 mb-0">{errorMessage}</div>}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BankAccountPublicEntry;
